<template>
  <div class="mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3>Import PAX</h3>
      <div>
        <d-button
          theme="secondary"
          class="mr-2"
          :disabled="isSubmitting"
          @click="$emit('cancel')">
          Cancel
        </d-button>
        <d-button
          theme="success"
          :disabled="isSubmitting || selectedPaxs.length === 0"
          @click="savePax">
          <i class="material-icons mr-1">save</i>
          <span>Save selected Pax ({{ selectedPaxs.length }})</span>
        </d-button>
      </div>
    </div>

    <div class="position-relative">
      <div
        v-if="isSubmitting"
        class="pb-3 pt-3 text-center overlay-loading"
        style="z-index: 2; border-radius: 0;">
        Saving selected records&hellip;
      </div>

      <div class="table-wrapper">
        <validation-observer
          ref="pax-import-form"
          tag="div">
          <table class="table table-striped table-bordered table-hover mt-3">
            <thead class="bg-light">
              <tr>
                <th class="is-sticky align-middle">
                  <d-checkbox
                    key="checkbox-select-all"
                    v-model="allSelected"
                    :disabled="isSubmitting"
                    @change="toggleSelectAll">
                  </d-checkbox>
                </th>
                <th class="align-middle">First name</th>
                <th class="align-middle">Last name</th>
                <th class="align-middle">DOB</th>
                <th class="align-middle">Place of birth</th>
                <th class="align-middle">Sex</th>
                <th class="align-middle">Customer</th>
                <th class="align-middle">Passport name</th>
                <th class="align-middle">Serial number</th>
                <th class="align-middle" style="width: 80px;">Nationality</th>
                <th class="align-middle" style="min-width: 220px;">Country</th>
                <th class="align-middle">Expiry</th>
                <th class="align-middle">Set as default</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(pax, index) in paxOutputData"
                :key="'pax-for-import-' + pax.id"
                :class="{
                  'table-success': pax._status.isSuccess,
                  'table-danger': pax._status.errorMessage !== null
                }">

                <td class="is-sticky align-middle text-center">
                  <div
                    v-if="pax._status.isSaving"
                    class="spinner-border spinner-border-sm text-primary"
                    role="status">
                    <span class="sr-only">Saving...</span>
                  </div>
                  <template v-else>
                    <div
                      v-if="pax._status.errorMessage"
                      class="mb-1">
                      <i
                        class="material-icons text-danger"
                        style="cursor: help;"
                        :title="pax._status.errorMessage">
                        error
                      </i>
                    </div>

                    <d-checkbox
                      :value="pax.id"
                      v-model="selectedPaxs"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    </d-checkbox>
                  </template>
                </td>

                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'first_name-' + index"
                    v-slot="{ errors }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      v-model="pax.passenger.first_name"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'last_name-' + index"
                    v-slot="{ errors }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      v-model="pax.passenger.last_name"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'birth_date-' + index"
                    v-slot="{ errors }">
                    <input
                      type="date"
                      lang="en-US"
                      class="form-control native-date-input"
                      :class="{ 'is-invalid': errors.length, 'has-value': !!pax.passenger.birth_date }"
                      :value="getNativeDate(pax.passenger.birth_date)"
                      @input="setNativeDate(pax.passenger, 'birth_date', $event.target.value)"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'birth_place-' + index"
                    v-slot="{ errors }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      v-model="pax.passenger.birth_place"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'sex-' + index"
                    v-slot="{ errors }">
                      <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                      <v-select
                        :options="sexOptions"
                        v-model="pax.passenger.sex"
                        :reduce="opt => opt.value"
                        label="text"
                        :clearable="false"
                        :disabled="pax._status.isSuccess || pax._status.isSaving"
                        append-to-body>
                      </v-select>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? '' : ''"
                    :name="'customer-' + index"
                    v-slot="{ errors }">
                    <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                      <v-select
                        :options="operators"
                        v-model="pax.customer_id"
                        :reduce="customer => customer.id"
                        :filterBy="filterByCustomers"
                        :maxItemsDisplay="100"
                        label="name"
                        :disabled="pax._status.isSuccess || pax._status.isSaving"
                        append-to-body>
                      </v-select>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'passport_name-' + index"
                    v-slot="{ errors }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      v-model="pax.passport.name"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'passport_serial_number-' + index"
                    v-slot="{ errors }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': errors.length }"
                      v-model="pax.passport.serial_number"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="countriesOptions"
                      v-model="pax.passport.country_id"
                      :reduce="country => country.id"
                      placeholder="Select a nationality"
                      :multiple="false"
                      label="text"
                      :disabled="pax._status.isSuccess || pax._status.isSaving"
                      append-to-body>
                      <template
                        slot="option"
                        slot-scope="option">
                        <div class="d-center">{{ option.text }}</div>
                      </template>
                      <template
                        slot="selected-option"
                        slot-scope="option">
                        <div class="selected d-center">
                          {{ option.iso_a3 || option.text }}
                        </div>
                      </template>
                      <template slot="no-options">
                        There are no nationalities for the provided phrase&hellip;
                      </template>
                    </v-select>
                  </div>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'passport_countries-' + index"
                    v-slot="{ errors }">
                    <div :class="{ 'form-control form-control-multiple': true, 'is-select': true, 'is-invalid': errors.length }">
                      <v-select
                        :options="countriesOptions"
                        v-model="pax.passport.countries"
                        :reduce="country => country.id"
                        placeholder="Select a country"
                        :multiple="true"
                        label="text"
                        :disabled="pax._status.isSuccess || pax._status.isSaving"
                        append-to-body>
                        <template slot="no-options">
                          There are no countries for the provided phrase&hellip;
                        </template>
                      </v-select>
                    </div>
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <validation-provider
                    :rules="selectedPaxs.includes(pax.id) ? 'required' : ''"
                    :name="'passport_expiry-' + index"
                    v-slot="{ errors }">
                    <input
                      type="date"
                      lang="en-US"
                      class="form-control native-date-input"
                      :class="{ 'is-invalid': errors.length, 'has-value': !!pax.passport.expiry }"
                      :value="getNativeDate(pax.passport.expiry)"
                      @input="setNativeDate(pax.passport, 'expiry', $event.target.value)"
                      :disabled="pax._status.isSuccess || pax._status.isSaving">
                    <div class="invalid-feedback">
                      {{ errors }}
                    </div>
                  </validation-provider>
                </td>
                <td>
                  <div class="form-control is-select">
                    <v-select
                      :options="isDefaultOptions"
                      v-model="pax.passport.is_default"
                      :reduce="item => item.value"
                      label="label"
                      :clearable="false"
                      :disabled="pax._status.isSuccess"
                      append-to-body>
                    </v-select>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </validation-observer>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import FormUtils from '@/utils/FormUtils';
import DateUtils from '@/utils/DateUtils.js';

export default {
  name: 'pax-import',
  props: {
    countriesOptions: {
      default: () => [],
      type: Array
    },
    operators: {
      default: () => [],
      type: Array
    },
    paxData: {
      required: true,
      type: Array
    }
  },
  data () {
    return {
      allSelected: false,
      isDefaultOptions: [{
        label: 'Yes',
        value: 1
      }, {
        label: 'No',
        value: 0
      }],
      isSubmitting: false,
      paxOutputData: [],
      selectedPaxs: [],
      sexOptions: [{
        text: 'Male',
        value: 'M'
      }, {
        text: 'Female',
        value: 'F'
      }]
    }
  },
  mounted () {
    const clonedData = JSON.parse(JSON.stringify(this.paxData));

    this.paxOutputData = clonedData.map(pax => {
      if (pax.passenger.birth_date) {
        pax.passenger.birth_date = new Date(pax.passenger.birth_date);
      }
      if (pax.passport.expiry) {
        pax.passport.expiry = new Date(pax.passport.expiry);
      }

      return {
        ...pax,
        _status: {
          isSaving: false,
          isSuccess: false,
          errorMessage: null
        }
      };
    });
  },
  methods: {
    async savePax () {
      const isValid = await this.$refs['pax-import-form'].validate();

      if (!isValid) {
        this.$nextTick(() => {
          const firstErrorElement = this.$el.querySelector('.is-invalid');
          if (firstErrorElement) {
            firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });

        return;
      }

      const paxesToSave = this.paxOutputData.filter(pax => this.selectedPaxs.includes(pax.id));

      if (paxesToSave.length === 0) {
        return;
      }

      this.isSubmitting = true;

      paxesToSave.forEach(pax => {
        this.saveSinglePax(pax);
      });
    },
    getCountryIso (id) {
      let country = this.countriesOptions.find(item => item.id === id);
      return country ? (country.iso_a3 || country.text) : id;
    },
    saveSinglePax (paxData) {
      paxData._status.isSaving = true;
      paxData._status.errorMessage = null;

      const passportNationality = this.getCountryIso(paxData.passport.country_id);

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/passengers/store-passenger-and-passport',
        formData: {
          passenger: {
            first_name: paxData.passenger.first_name,
            last_name: paxData.passenger.last_name,
            birth_date: DateUtils.convertDateToString(paxData.passenger.birth_date),
            birth_place: paxData.passenger.birth_place,
            sex: paxData.passenger.sex,
            flight_customer_id: paxData.customer_id
          },
          passport: {
            name: paxData.passport.name,
            serial_number: paxData.passport.serial_number,
            country_id: paxData.passport.country_id || 0,
            nationality: passportNationality,
            countries: paxData.passport.countries,
            expiry: DateUtils.convertDateToString(paxData.passport.expiry),
            is_default: paxData.passport.is_default,
          },
          customer_id: paxData.customer_id // TODO:
        },
        successAction: (response) => {
          if (response && response.data && response.data.success === false) {
            paxData._status.isSaving = false;
            paxData._status.isSuccess = false;
            paxData._status.errorMessage = response.data.message || 'Validation failed.';
          } else {
            paxData._status.isSaving = false;
            paxData._status.isSuccess = true;
            this.selectedPaxs = this.selectedPaxs.filter(id => id !== paxData.id);
          }
          this.checkBatchComplete();
        },
        failAction: (error) => {
          let msg = 'Failed to save record.';

          if (error.response && error.response.data && error.response.data.message) {
            msg = error.response.data.message;
          }

          paxData._status.isSaving = false;
          paxData._status.isSuccess = false;
          paxData._status.errorMessage = msg;
          this.checkBatchComplete();
        }
      });
    },
    checkBatchComplete () {
      const isAnythingStillSaving = this.paxOutputData.some(pax => pax._status.isSaving);

      if (!isAnythingStillSaving) {
        this.isSubmitting = false;

        const successCount = this.paxOutputData.filter(pax => pax._status.isSuccess).length;
        const failedCount = this.paxOutputData.filter(pax => pax._status.errorMessage !== null).length;

        const allSaved = this.paxOutputData.every(pax => pax._status.isSuccess);

        const swalConfig = {
          title: failedCount > 0 ? 'Import finished with errors' : 'Success',
          html: `Records successfully saved: <b>${successCount}</b><br>Records failed to save: <b>${failedCount}</b>`,
          icon: failedCount > 0 ? 'warning' : 'success',
          buttonsStyling: true
        };

        if (failedCount === 0) {
          swalConfig.timer = 1500;
          swalConfig.timerProgressBar = true;
        }

        Vue.swal(swalConfig).then(() => {
          if (allSaved) {
            this.$emit('cancel');
          }
        });
      }
    },
    toggleSelectAll (newVal) {
      if (newVal) {
        Vue.set(
          this,
          'selectedPaxs',
          this.paxOutputData
            .filter(item => !item._status.isSuccess)
            .map(item => item.id)
        );
        return;
      }
      Vue.set(this, 'selectedPaxs', []);
    },
    filterByCustomers (option, label, search) {
      return option.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    getNativeDate (dateValue) {
      if (!dateValue) {
        return '';
      }

      const expiryDate = new Date(dateValue);

      if (expiryDate.toString() === 'Invalid Date') {
        return '';
      }

      const year = expiryDate.getFullYear();
      const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
      const day = String(expiryDate.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    setNativeDate (targetObject, fieldName, val) {
      if (!val) {
        targetObject[fieldName] = null;
        return;
      }
      targetObject[fieldName] = new Date(val);
    }
  }
}
</script>

<style lang="scss" scoped>
.table-wrapper {
  overflow-x: auto;
  width: 100%;
  position: relative;
}

.is-sticky {
  position: sticky;
  left: 0;
  z-index: 2;
}

th.is-sticky,
td.is-sticky {
  background-color: #fff;

  &:after,
  &:before {
    background-color: #dee2e6;
    content: "";
    height: 100%;
    position: absolute;
    top: 0;
    width: 2px;
  }

  &:before {
    left: -1px;
  }
  &:after {
    right: 0;
  }
}

.table-striped tbody tr:nth-of-type(odd) td.is-sticky {
  background-color: #f4f4f4;
}

.table thead th:not(.is-sticky) {
  min-width: 170px;
}

.table thead th.is-sticky {
  min-width: 50px;
}

.native-date-input {
  position: relative;
  text-transform: uppercase;
  color: #495057;

  &::-webkit-datetime-edit {
    padding: 0;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    transition: 0.2s;
    &:hover {
      opacity: 1;
    }
  }

  &:not(.has-value) {
    color: transparent;
  }

  &:not(.has-value)::-webkit-calendar-picker-indicator {
    opacity: 1;
  }
}
</style>
